import { SseEvent } from '@evidentid/tprm-portal-lib/models/evie/SseEvent.model';
import createCustomXhrErrorFactory from '@evidentid/universal-framework/createCustomXhrErrorFactory';
import parseJsonConditionally from '@evidentid/universal-framework/parseJsonConditionally';

import { TprmUiService, OpsUiService } from './services';
import { SubmissionTprmUiService } from './services/SubmissionTprmUiService';

const statusErrorFactories: Record<number, (xhr?: XMLHttpRequest) => Error> = {
    401: createCustomXhrErrorFactory('unauthorized', 'You are not authorized for selected operation'),
    403: createCustomXhrErrorFactory('forbidden', 'You are not authorized for selected operation'),
    404: createCustomXhrErrorFactory('not-found', 'The requested resource is not found'),
    440: createCustomXhrErrorFactory('session-expired', 'Your session has expired'),
};

interface ApiClientTokens {
    accessToken: string | null;
    idToken: string | null;
}

// Carries the HTTP status on a rejection so callers can tell a 400 from a 500. The rejection
// value stays the parsed response body, so reading `error`, `message` etc. off it still works.
export const HTTP_STATUS_PROPERTY = 'httpStatus';

export function getHttpStatus(error: unknown): number | null {
    return typeof error === 'object' && error !== null && HTTP_STATUS_PROPERTY in error
        ? (error as Record<string, number>)[HTTP_STATUS_PROPERTY]
        : null;
}

function withHttpStatus(body: any, status: number): any {
    // A non-object body (empty response, plain text) is passed through untouched — the errors
    // worth telling apart all answer with a JSON body, so there is nothing to gain from wrapping
    // it, and wrapping would change the rejection's shape for every caller.
    return body !== null && typeof body === 'object'
        ? Object.assign(body, { [HTTP_STATUS_PROPERTY]: status })
        : body;
}

export class GatewayApiClient {
    protected baseUrl: string;
    protected getTokens: () => Promise<ApiClientTokens> = () => Promise.resolve({
        accessToken: null,
        idToken: null,
    });

    private readonly tprmUiService: TprmUiService;
    private readonly opsUiService: OpsUiService;
    private readonly submissionTprmUiService: SubmissionTprmUiService;

    public constructor(baseUrl: string) {
        this.baseUrl = baseUrl.replace(/\/+$/, '');
        this.tprmUiService = new TprmUiService(
            this.request.bind(this),
            this.streamingRequest.bind(this),
            this.binaryRequest.bind(this),
        );
        this.opsUiService = new OpsUiService(this.request.bind(this));
        this.submissionTprmUiService = new SubmissionTprmUiService(this.request.bind(this));
    }

    public setTokens(token: () => (Promise<ApiClientTokens> | ApiClientTokens)): this {
        this.getTokens = () => Promise.resolve(token());
        return this;
    }

    public tprmUi(): TprmUiService {
        return this.tprmUiService;
    }

    public opsUi(): OpsUiService {
        return this.opsUiService;
    }

    public submissionTprmUi(): SubmissionTprmUiService {
        return this.submissionTprmUiService;
    }

    private parseSseBlock(block: string): SseEvent | null {
        if (!block.trim()) {
            return null;
        }
        let eventType = '';
        let dataLine = '';
        for (const line of block.split('\n')) {
            if (line.startsWith('event:')) {
                eventType = line.slice('event:'.length).trim();
            } else if (line.startsWith('data:')) {
                dataLine = line.slice('data:'.length).trim();
            }
        }
        if (!eventType || !dataLine) {
            return null;
        }
        try {
            const payload = JSON.parse(dataLine);
            return { type: eventType, data: payload } as SseEvent;
        } catch {
            return null; // malformed SSE data
        }
    }

    // GET for a binary body. `request` decodes every response as JSON text, which would corrupt
    // image bytes, so this reads the body as a Blob; auth headers and status handling match it.
    protected async binaryRequest(requestUrl: string): Promise<Blob> {
        const { accessToken, idToken } = await this.getTokens();
        const headers: Record<string, string> = { 'cache-control': 'no-cache' };
        if (accessToken) {
            headers.authorization = `Bearer ${accessToken}`;
        }
        if (idToken) {
            headers.idtoken = idToken;
        }
        const response = await fetch(requestUrl.startsWith('http') ? requestUrl : `${this.baseUrl}${requestUrl}`, {
            method: 'GET',
            headers,
        });
        const statusErrorFactory = statusErrorFactories[response.status];
        if (statusErrorFactory) {
            throw statusErrorFactory();
        }
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        return response.blob();
    }

    protected async streamingRequest(
        requestUrl: string,
        data: unknown,
        onEvent: (event: SseEvent) => void,
        options: { signal?: AbortSignal, inactivityMs?: number } = {},
    ): Promise<void> {
        const { signal, inactivityMs = 30_000 } = options;
        const { accessToken, idToken } = await this.getTokens();

        const headers: Record<string, string> = {
            'content-type': 'application/json',
            'accept': 'text/event-stream',
            'cache-control': 'no-cache',
        };
        if (accessToken) {
            headers.authorization = `Bearer ${accessToken}`;
        }
        if (idToken) {
            headers.idtoken = idToken;
        }

        // Inactivity timeout: abort if no chunk arrives within inactivityMs.
        // Combined with any caller-supplied signal (conversation switch / RP switch).
        const inactivityController = new AbortController();
        let inactivityTimer = setTimeout(() => inactivityController.abort(), inactivityMs);
        const resetInactivityTimer = () => {
            clearTimeout(inactivityTimer);
            inactivityTimer = setTimeout(() => inactivityController.abort(), inactivityMs);
        };

        // AbortSignal.any is not available in TypeScript 4.x — wire signals manually.
        if (signal) {
            if (signal.aborted) {
                inactivityController.abort();
            } else {
                signal.addEventListener('abort', () => inactivityController.abort(), { once: true });
            }
        }
        const combinedSignal = inactivityController.signal;

        let response: Response;
        try {
            response = await fetch(requestUrl.startsWith('http') ? requestUrl : `${this.baseUrl}${requestUrl}`, {
                method: 'POST',
                headers,
                body: JSON.stringify(data),
                signal: combinedSignal,
            });
        } finally {
            clearTimeout(inactivityTimer);
        }
        // Timer was cleared once the fetch connected; restart it so a stall before
        // the first body chunk is also caught by the inactivity timeout.
        resetInactivityTimer();

        // Check pre-stream HTTP errors — same codes handled by XHR request()
        const statusFactories = statusErrorFactories as Record<number, (xhr?: XMLHttpRequest) => Error>;
        const preStreamErrorFactory = statusFactories[response.status];
        if (preStreamErrorFactory) {
            throw preStreamErrorFactory();
        }
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) {
                    break;
                }

                resetInactivityTimer();
                buffer += decoder.decode(value, { stream: true });

                // SSE blocks are separated by double newlines
                const blocks = buffer.split('\n\n');
                buffer = blocks.pop()!; // last element may be an incomplete block

                for (const block of blocks) {
                    const event = this.parseSseBlock(block);
                    if (event) {
                        onEvent(event);
                    }
                }
            }
        } finally {
            clearTimeout(inactivityTimer);
            reader.releaseLock();
        }
    }

    private async request<T = any>(
        method: string,
        requestUrl: string,
        data?: any,
        allowedAdditionalHttpStatuses: number[] = [],
    ): Promise<T> {
        // Retrieve current auth token
        const { accessToken, idToken } = await this.getTokens();

        return new Promise((resolve, reject) => {
            // Initialize XHR object
            const xhr = new XMLHttpRequest();
            xhr.open(method, requestUrl.startsWith('http') ? requestUrl : `${this.baseUrl}${requestUrl}`);
            xhr.setRequestHeader('content-type', 'application/json');
            if (accessToken) {
                xhr.setRequestHeader('authorization', `Bearer ${accessToken}`);
            }
            if (idToken) {
                xhr.setRequestHeader('idtoken', idToken);
            }
            xhr.setRequestHeader('cache-control', 'no-cache');

            // Handle valid response (read as JSON, but fallback to auth error or responseText)
            xhr.addEventListener('load', () => {
                const statusErrorFactory = statusErrorFactories[xhr.status];
                if (statusErrorFactory) {
                    return reject(statusErrorFactory(xhr));
                }
                const result = parseJsonConditionally(xhr.responseText);
                const success = (
                    (xhr.status >= 200 && xhr.status < 300) ||
                    allowedAdditionalHttpStatuses.includes(xhr.status)
                );
                return success ? resolve(result) : reject(withHttpStatus(result, xhr.status));
            });

            // Handle connection problem
            xhr.addEventListener('error', (error) => {
                console.error(`Config Api Client request error: ${requestUrl} [${xhr.status}]`, error);
                reject(error);
            });

            // Initialize request
            if (data === undefined) {
                xhr.send();
            } else {
                xhr.send(JSON.stringify(data));
            }
        });
    }
}
